import './SystemOverview.scss';

import { ApiRequests } from '../ApiRequests';
import { AppStatusSelectValue } from '@security/AppStatusSelect';
import { createAsyncSelectors } from '@lib/selectors';
import empty from 'images/empty-overview-apps.svg';
import { GraphBehavior } from './GraphBehaviors';
import { GraphLegend } from './GraphLegend';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import { OrganizeBySelectValue } from './OrganizeBySelect';
import Salesforce from 'images/salesforce-logo.png';
import { SonarText } from '@ui/text';
import uniqBy from 'lodash/uniqBy';
import { ViewConnectedAppsButton } from './ViewConnectedAppsButton';

import { AppLogoMetadata, AppLogoMetadataLookup } from './AppLogoMetadata';
import { buildEdge, buildImageMetadata, OverviewGraph } from './OverviewGraph';
import {
	CLASSIFICATION_COLORS,
	COPY_SPECIFIC,
	DATA_SECURITY_DASHBOARD,
	TAG_COLORS
} from '@sonar-software/sonar-ts-constants';
import { Col, InputNumber, Row, Space } from 'antd';
import { Combo, IUserEdge, IUserNode } from '@antv/graphin/lib/typings/type';
import {
	coreActions,
	DataSecurityApplication,
	DataSecurityApplicationOverview,
	DataSecurityApplicationStatus,
	defaultComboForceOptions,
	emptyGraphData,
	FeatureFlag
} from '@sonar-software/sonar-core';
import { Else, If, Then, When } from 'react-if';
import {
	GraphConfigEditor,
	GraphLayouts,
	GraphLayoutSelector,
	GraphLines,
	GraphLineSelector
} from '@security/graph-tools';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { SanitizedHtml, SonarCard } from '@ui/general';
import { useAsyncStateWithPreload, useDataSecurityApps, useFeatureFlag, useSonarSelector } from '@hooks';

async function buildImageMetadataLookup(apps: Array<DataSecurityApplication>): Promise<AppLogoMetadataLookup> {
	const results: Array<Promise<AppLogoMetadata>> = apps.map((app) =>
		buildImageMetadata(app.applicationId, app.name, app.label));

	const allResults = await Promise.all(results);

	return allResults.reduce((prev, next) => {
		prev[ next.id ] = next;

		return prev;
	}, {} as Record<number, AppLogoMetadata>);
}

type ComboDefinition = Combo & {
	id: string,
	type: 'rect' | 'circle'
};

function buildGroupCombos(
	entries: Array<DataSecurityApplicationOverview>
): Array<ComboDefinition> {
	const combos: Array<ComboDefinition> =
		uniqBy(entries, (entry) => entry.stakeholderGroupId)
			.filter((entry) => Boolean(entry.stakeholderGroupId))
			.map((entry, index) => ({
				id: `${entry.stakeholderGroupId ?? 0}-combo`,
				style: {
					fill: `${TAG_COLORS[ index % TAG_COLORS.length ]}19`,
					stroke: TAG_COLORS[ index % TAG_COLORS.length ]
				},
				type: 'rect'
			}));

	if (entries.some((entry) => entry.status === DataSecurityApplicationStatus.Detected)) {
		combos.push({
			id: 'detected-combo',
			type: 'rect'
		});
	}

	return combos;
}

function buildClassificationCombos(entries: Array<DataSecurityApplicationOverview>): Array<ComboDefinition> {
	return uniqBy(entries, (entry) => entry.classification)
		.filter((entry) => Boolean(entry.classification))
		.map((entry) => entry.classification ?? '')
		.map((entry) => ({
			id: `${entry}-combo`,
			style: {
				fill: `${CLASSIFICATION_COLORS[ entry ]}19`,
				stroke: CLASSIFICATION_COLORS[ entry ]
			},
			type: 'rect'
		}));
}

function buildEdges(
	entries: Array<DataSecurityApplicationOverview>,
	lineType: string
): Array<IUserEdge> {
	return entries
		.map((entry) => buildEdge(entry.read, entry.write, lineType, String(entry.applicationId), 'salesforce'));
}

function getComboForNode(
	entry: DataSecurityApplicationOverview,
	groupByType: OrganizeBySelectValue
): string | undefined {
	if (entry.status === DataSecurityApplicationStatus.Detected) {
		return 'detected-combo';
	}

	const comboId = groupByType === 'group' ? entry.stakeholderGroupId : entry.classification;

	return comboId ? `${comboId}-combo` : undefined;
}

function buildNodes(
	entries: Array<DataSecurityApplicationOverview>,
	appLookup: AppLogoMetadataLookup,
	organizeByType: OrganizeBySelectValue
): Array<IUserNode> {
	return [
		{
			id: 'salesforce',
			img: Salesforce,
			size: [ 178, 121 ],
			type: 'image'
		},
		...entries
			.map((entry) => ({
				comboId: getComboForNode(entry, organizeByType),
				detected: entry.status === DataSecurityApplicationStatus.Detected,
				id: String(entry.applicationId),
				metadata: appLookup[ entry.applicationId ] ?? {
					name: `Undefined App ${entry.applicationId}`
				},
				type: 'app-display'
			}))
	];
}

const appOverviewSelectors = createAsyncSelectors((state) => state.core.dataSecurity.applicationOverview.get);
const getDataSecurityApiRequestUsageSelector =
	createAsyncSelectors((state) => state.core.dataSecurity.apiRequestUsage.get);

export interface SystemOverviewProps {
	expanded?: boolean;
}

export const SystemOverview: React.FC<SystemOverviewProps> = memo(({ expanded = false }) => {
	const displayTools = useFeatureFlag(FeatureFlag.DataSecurityGraphTools);

	const [ appStatus, setAppStatus ] = useState<AppStatusSelectValue>(AppStatusSelectValue.all);
	const [ hideDetected, setHideDetected ] = useState(false);
	const [ organizeBy, setOrganizeBy ] = useState<OrganizeBySelectValue>('group');
	const [ layoutType, setLayoutType ] = useState(GraphLayouts[ 3 ].value);
	const [ lineType, setLineType ] = useState(GraphLines[ 12 ].value);
	const [ mockAppCount, setMockAppCount ] = useState(0);
	const [ appMetadata, setAppMetadata ] = useState<AppLogoMetadataLookup | null>(null);
	const [ maxZoom, setMaxZoom ] = useState<number>(2);
	const [ minZoom, setMinZoom ] = useState<number>(0.25);

	const [ layoutOptions, setLayoutOptions ] = useState<Record<string, unknown>>(
		{
			...defaultComboForceOptions,
			type: layoutType
		}
	);

	const applications = useDataSecurityApps(!expanded, true);
	const appsLoaded = useSonarSelector(useDataSecurityApps.selectors.isReceived);

	const { data: apiRequestData } = useAsyncStateWithPreload(
		getDataSecurityApiRequestUsageSelector,
		coreActions.requestGetCoreDataSecurityApiRequestUsage,
		!expanded
	);

	const { data } = useAsyncStateWithPreload(
		appOverviewSelectors,
		coreActions.requestGetCoreDataSecurityApplicationOverview,
		!expanded
	);

	useEffect(() => {
		if (!appMetadata && appsLoaded) {
			buildImageMetadataLookup(applications).then((result) => {
				setAppMetadata(result);
			});
		}
	}, [ appMetadata, applications, appsLoaded ]);

	const graphData = useMemo(() => {
		const mergedData: Array<DataSecurityApplicationOverview> | null | undefined = data ? [
			...data.map((entry) => ({
				...entry,
				classification: applications.find(
					(app) => app.applicationId === entry.applicationId
				)?.classification?.toLowerCase()
			})),
			...Array.from(Array(mockAppCount).keys()).map((val) => ({
				applicationId: val + 500,
				installed: false,
				read: 1,
				status: DataSecurityApplicationStatus.Detected,
				write: 0
			}))
		] : [];

		const filteredData = mergedData
			.filter((entry) => {
				if (hideDetected && entry.status === DataSecurityApplicationStatus.Detected) {
					return false;
				}

				if (appStatus === AppStatusSelectValue.connected) {
					return !entry.installed;
				}

				if (appStatus === AppStatusSelectValue.installed) {
					return entry.installed;
				}

				return true;
			});

		return {
			combos: organizeBy === 'group' ?
				buildGroupCombos(filteredData) : buildClassificationCombos(filteredData),
			edges: buildEdges(filteredData, lineType),
			nodes: buildNodes(filteredData, appMetadata ?? {}, organizeBy)
		};
	}, [ appMetadata, applications, data, organizeBy, hideDetected, lineType, mockAppCount, appStatus ]);

	const isReady = data && appMetadata;
	const showGraph = data?.length || mockAppCount;

	return (
		<SonarCard id='system-overview-card'>
			<Row>
				<Col span={ 12 }>
					<Space
						className='system-overview-buttons'
						direction='vertical'
						size='small'>
						<div className='h2'>{ DATA_SECURITY_DASHBOARD.SYSTEM_OVERVIEW }</div>
						<ViewConnectedAppsButton />
					</Space>
				</Col>
				{
					apiRequestData?.dailyApiRequests &&
						<Col span={ 12 }>
							<ApiRequests
								header={ DATA_SECURITY_DASHBOARD.API_REQUESTS_LAST_24 }
								maxCalls={ apiRequestData.dailyApiRequests.max }
								tooltip={ <SanitizedHtml
									as='span'
									html={ DATA_SECURITY_DASHBOARD.API_REQUESTS_TOOLTIP } /> }
								usedCalls={ apiRequestData?.dailyApiRequests?.max -
					apiRequestData?.dailyApiRequests?.remaining } />
						</Col>
				}
			</Row>
			<LoadingSpinner
				minDuration={ 1500 }
				opaque
				spinning={ !isReady }>
				<If condition={ showGraph }>
					<Then>
						<OverviewGraph
							data={ isReady ? graphData : emptyGraphData }
							layoutOptions={ layoutOptions }
							maxZoom={ maxZoom }
							minZoom={ Number(minZoom) }>
							<GraphBehavior />
							<GraphLegend
								appStatus={ appStatus }
								expanded={ expanded }
								extra={
									<When condition={ displayTools }>
										<Space
											className='extra-header'
											direction='vertical'>
											<div className='groups-header'>{ COPY_SPECIFIC.ADMINISTRATION }</div>
											<Space>
												<GraphLayoutSelector
													onChange={ (value) => {
														setLayoutOptions({
															...layoutOptions,
															type: value
														});
														setLayoutType(value);
													} }
													value={ layoutType } />
												<GraphLineSelector
													onChange={ setLineType }
													value={ lineType } />
												<InputNumber
													min={ 0 }
													onChange={ (value) => setMockAppCount(value ?? 0) }
													value={ mockAppCount } />
												<InputNumber
													min={ 0 }
													onChange={ (value) => setMaxZoom(value ?? 2) }
													value={ maxZoom } />
												<InputNumber
													min={ 0 }
													onChange={ (value) => setMinZoom(value ?? 0.25) }
													step={ 0.1 }
													stringMode
													value={ minZoom } />
											</Space>
											<GraphConfigEditor
												onChange={ setLayoutOptions }
												value={ layoutOptions } />
										</Space>
									</When>
								}
								groupIds={ graphData.combos.map((entry) => entry.id) }
								hideDetected={ hideDetected }
								onAppStatusChange={ setAppStatus }
								onHideDetectedChange={ setHideDetected }
								onOrganizeByChange={ setOrganizeBy }
								organizeBy={ organizeBy } />
						</OverviewGraph>
					</Then>
					<Else>
						<div className='empty-graph'>
							<Space
								direction='vertical'
								size='large'>
								<SonarText
									size={ 16 }
									weight='semibold'>{ COPY_SPECIFIC.NO_CONNECTED_APPS }
								</SonarText>
								<img
									alt={ COPY_SPECIFIC.NO_CONNECTED_APPS }
									className='empty-apps'
									src={ empty } />
							</Space>
						</div>
					</Else>
				</If>
			</LoadingSpinner>
		</SonarCard>
	);
});
